import { Component, inject, output, signal, TemplateRef, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { FileInputType, Language, PageMode } from "../../../constants/enums";
import { AttachFile } from "../../../models";
import { ContextualHelpContent } from "../../../models/online-help-model";
import { AppService } from "../../../services";
import {
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormInputComponent,
  FormInputFileComponent,
} from "../../form";
import { QuillEditorComponent } from "../../quill-editor/quill-editor";

@Component({
  selector: "adaa-contextual-help-content-editor",
  imports: [
    FormInputComponent,
    FormActionButtonsComponent,
    TranslatePipe,
    ReactiveFormsModule,
    FormInputFileComponent,
    FormCheckboxComponent,
    QuillEditorComponent,
  ],
  templateUrl: "./contextual-help-content-editor.html",
  styleUrl: "./contextual-help-content-editor.scss",
})
export class ContextualHelpContentEditorComponent {
  private _formBuilder = inject(FormBuilder);
  private _modalService = inject(NgbModal);
  private _appService = inject(AppService);
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);

  contentEditorForm: FormGroup = new FormGroup({});
  submitted = signal<boolean>(false);
  modal = viewChild.required<TemplateRef<unknown>>("content");
  pageMode = signal<PageMode>(PageMode.create);
  content = output<ContextualHelpContent>();
  modalRef: NgbModalRef;
  PageMode = PageMode;
  FileInputType = FileInputType;
  contentAEEditor = viewChild.required<QuillEditorComponent>("contentAE");
  contentENEditor = viewChild.required<QuillEditorComponent>("contentEN");
  Language = Language;
  fileTypes = [FileInputType.jpeg, FileInputType.jpg, FileInputType.png, FileInputType.gif];

  public ngOnInit(): void {
    this._prepareForm();
  }

  public imageLabel(lang: Language): string {
    return (
      (lang === Language.Arabic
        ? this._translateService.instant("help.help_modal.imageAE")
        : this._translateService.instant("help.help_modal.imageEN")) +
      "\n" +
      this._translateService.instant("help.help_modal.image_only_info")
    );
  }

  private _prepareForm() {
    this.contentEditorForm = this._formBuilder.group({
      titleAE: this._formBuilder.control<string>(""),
      titleEN: this._formBuilder.control<string>(""),
      helpContentAE: this._formBuilder.control<string>(""),
      helpContentEN: this._formBuilder.control<string>(""),
      published: this._formBuilder.control<string>(""),
      imageOnly: this._formBuilder.control<string>(""),
      imageIdAE: this._formBuilder.control<number | null>(null),
      imageIdEN: this._formBuilder.control<number | null>(null),
      filenameAE: this._formBuilder.control<string>(""),
      filenameEN: this._formBuilder.control<string>(""),
    });
  }

  public open() {
    const modalRef = this._modalService.open(this.modal(), {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "lg",
      centered: true,
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    this.contentAEEditor().content = this.contentEditorForm.value.helpContentAE;
    this.contentENEditor().content = this.contentEditorForm.value.helpContentEN;
    this.modalRef = modalRef;
    modalRef.dismissed.subscribe(() => this._resetForm());
    modalRef.closed.subscribe(() => this._resetForm());
  }

  public submit() {
    this.submitted.set(true);
    if (this.contentEditorForm.invalid) {
      this._toastrService.error(this._translateService.instant("notification.warning.missing_info"));
      return;
    }

    this.content.emit(this.contentEditorForm.value);
    this.modalRef.close();
  }

  public checkInvalid(control: string) {
    const result =
      this.contentEditorForm.controls[control]?.errors?.["required"] &&
      (this.contentEditorForm.get(control)?.dirty || this.submitted());
    return result;
  }

  private _resetForm() {
    this.contentEditorForm.reset();
    this.contentEditorForm.markAsPristine();
    this.contentEditorForm.markAsUntouched();
    this.submitted.set(false);
  }

  public getImage(lang: Language) {
    const id =
      lang === Language.Arabic ? this.contentEditorForm.value.imageIdAE : this.contentEditorForm.value.imageIdEN;
    const filename =
      lang === Language.Arabic ? this.contentEditorForm.value.filenameAE : this.contentEditorForm.value.filenameEN;
    return {
      id: id ?? 0,
      filename: filename ?? "",
    };
  }

  public onFileChange(event: AttachFile | null, lang: Language) {
    if (event) {
      this.contentEditorForm.get(lang === Language.Arabic ? "imageIdAE" : "imageIdEN")?.setValue(event.id);
      this.contentEditorForm.get(lang === Language.Arabic ? "filenameAE" : "filenameEN")?.setValue(event.filename);
    } else {
      this.contentEditorForm.get(lang === Language.Arabic ? "imageIdAE" : "imageIdEN")?.setValue(null);
      this.contentEditorForm.get(lang === Language.Arabic ? "filenameAE" : "filenameEN")?.setValue(null);
    }
  }

  public setValue(event: string, lang: Language) {
    const control = lang === Language.Arabic ? "helpContentAE" : "helpContentEN";
    this.contentEditorForm.get(control)?.patchValue(event);
  }
}
